import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { Mail, User, Folder, List, ShieldCheck, Users, CreditCard, Search, Filter } from "lucide-react";
import "./App.css";
import logo from "./logo.png";

function App() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [accountsData, setAccountsData] = useState([]);
  const [editMode, setEditMode] = useState({});
  const [formData, setFormData] = useState({});
  
  // 🔍 NEW: Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("Contains"); // Default filter

  const categoryOptions = [
    "Development",
    "Production",
    "Infrastructure",
    "Sandbox",
    "Shared",
    "Suspended",
    "Staging",
  ];

  const filterOptions = ["Contains", "Starts With", "Ends With", "Exact Match"];

  const apiUrl = "https://qp8f4ovspb.execute-api.eu-west-1.amazonaws.com/prod";

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const userRole = account?.idTokenClaims?.roles?.includes("Admin") ? "Admin" : "Viewer";

  useEffect(() => {
    if (account) {
      fetchData();
    }
  }, [account]);

  const fetchData = async () => {
    try {
      const res = await fetch(`${apiUrl}/accounts`);
      if (!res.ok) {
        throw new Error(`API Error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      setAccountsData(data);
    } catch (err) {
      console.error("Error loading data:", err);
    }
  };

  const handleEdit = (accountNumber) => {
    setEditMode((prev) => ({ ...prev, [accountNumber]: true }));
    const accountItem = accountsData.find((acc) => acc.accountNumber === accountNumber);
    setFormData((prev) => ({ ...prev, [accountNumber]: { ...accountItem } }));
  };

  const handleChange = (accountNumber, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [accountNumber]: {
        ...prev[accountNumber],
        [field]: value,
      },
    }));
  };

  const handleSave = async (accountNumber) => {
    const updated = formData[accountNumber];
    try {
      const res = await fetch(`${apiUrl}/saved`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accountNumber,
          name: updated.name,
          email: updated.email,
          category: updated.category,
          projectOwner: updated.projectOwner,
          projectOwnerEmail: updated.projectOwnerEmail,
          altContactName: updated.altContactName,
          altContactEmail: updated.altContactEmail,
          costCenter: updated.costCenter,
        }),
      });

      if (res.ok) {
        setEditMode((prev) => ({ ...prev, [accountNumber]: false }));
        fetchData();
      } else {
        const errorText = await res.text();
        console.error("Save failed:", errorText);
      }
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  // 🔍 NEW: Highlighting Component
  const HighlightText = ({ text }) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.toString().split(regex);

    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: "#FFFF00", fontWeight: "bold" }}>{part}</span>
      ) : (
        part
      )
    );
  };

  // 🔍 NEW: Filter Logic
  const getFilteredAccounts = () => {
    if (!searchTerm) return accountsData;

    return accountsData.filter((acc) => {
      // Combine all searchable fields into one string for easy checking
      const searchableValues = [
        acc.accountNumber,
        acc.name,
        acc.email,
        acc.category,
        acc.projectOwner,
        acc.projectOwnerEmail,
        acc.altContactName,
        acc.altContactEmail,
        acc.costCenter
      ].map(val => (val || "").toString().toLowerCase());

      const term = searchTerm.toLowerCase();

      // Check if ANY field matches the condition
      return searchableValues.some(val => {
        if (filterType === "Contains") return val.includes(term);
        if (filterType === "Starts With") return val.startsWith(term);
        if (filterType === "Ends With") return val.endsWith(term);
        if (filterType === "Exact Match") return val === term;
        return false;
      });
    });
  };

  const filteredAccounts = getFilteredAccounts();

  if (!account) {
    return (
      <div className="App" style={{ textAlign: 'center', marginTop: '100px' }}>
        <img src={logo} alt="Logo" style={{ height: '80px', marginBottom: '20px' }} />
        <h2>AWS Account Ownership Viewer</h2>
        <button onClick={handleLogin}>Login with Azure AD</button>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={logo} alt="Logo" style={{ height: '50px' }} />
          <h2>AWS Account Ownership Viewer</h2>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
           <p style={{ margin: 0, fontWeight: '500' }}>
            Welcome, {account.name && account.name.includes(userRole) ? account.name : `${account.name || 'User'} (${userRole})`}
          </p>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* 🔍 SEARCH BAR UI */}
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '15px 20px', 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center', 
        gap: '10px',
        borderBottom: '1px solid #ddd'
      }}>
        <Search size={18} color="#666" />
        <input 
          type="text" 
          placeholder="Search..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '8px', width: '300px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
        
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Filter size={16} style={{ position: 'absolute', left: '8px', zIndex: 1 }} />
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            style={{ padding: '8px 8px 8px 30px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer' }}
          >
            {filterOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <table className="account-table" style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr style={{ backgroundColor: '#E3EDFB', textAlign: 'left' }}>
            <th style={{ width: '12%', padding: '10px' }}><List size={16} /> Account No</th>
            <th style={{ width: '12%', padding: '10px' }}><Folder size={16} /> Account Name</th>
            <th style={{ width: '14%', padding: '10px' }}><Mail size={16} /> Root Email</th>
            <th style={{ width: '10%', padding: '10px' }}><List size={16} /> Category</th>
            <th style={{ width: '10%', padding: '10px' }}><User size={16} /> Project Owner</th>
            <th style={{ width: '14%', padding: '10px' }}><Mail size={16} /> Project Owner Email</th>
            <th style={{ width: '10%', padding: '10px' }}><Users size={16} /> Alt Contact Name</th>
            <th style={{ width: '14%', padding: '10px' }}><Mail size={16} /> Alt Contact Email</th>
            <th style={{ width: '10%', padding: '10px' }}><CreditCard size={16} /> Cost Center</th>
            <th style={{ width: '4%', padding: '10px' }}><ShieldCheck size={16} /> Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAccounts.length > 0 ? (
            filteredAccounts.map((acc) => (
              <tr key={acc.accountNumber}>
                <td style={{ padding: '10px' }}><HighlightText text={acc.accountNumber} /></td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.name || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "name", e.target.value)}
                  />) : <HighlightText text={acc.name} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.email || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "email", e.target.value)}
                  />) : <HighlightText text={acc.email} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <select
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.category || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "category", e.target.value)}
                  >
                    <option value="">Select</option>
                    {categoryOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>) : <HighlightText text={acc.category} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.projectOwner || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "projectOwner", e.target.value)}
                  />) : <HighlightText text={acc.projectOwner} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.projectOwnerEmail || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "projectOwnerEmail", e.target.value)}
                  />) : <HighlightText text={acc.projectOwnerEmail} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.altContactName || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "altContactName", e.target.value)}
                  />) : <HighlightText text={acc.altContactName} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.altContactEmail || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "altContactEmail", e.target.value)}
                  />) : <HighlightText text={acc.altContactEmail} />}</td>
                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <input
                    style={{ width: '100%' }}
                    value={formData[acc.accountNumber]?.costCenter || ""}
                    onChange={(e) => handleChange(acc.accountNumber, "costCenter", e.target.value)}
                  />) : <HighlightText text={acc.costCenter} />}
                </td>

                <td style={{ padding: '10px' }}>{editMode[acc.accountNumber] ? (
                  <button 
                    onClick={() => handleSave(acc.accountNumber)}
                    disabled={userRole !== "Admin"}
                  >Save</button>) : (
                  <button 
                    onClick={() => handleEdit(acc.accountNumber)}
                    disabled={userRole !== "Admin"}
                  >Edit</button>)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                No matching records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default App;